import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { API_ADMIN } from '../config';

interface AuthState {
  authenticated: boolean;
  username: string;
  loading: boolean;
}

interface AuthContextType extends AuthState {
  login: (username: string, password: string) => Promise<{ success: boolean; message: string }>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<AuthState>({
    authenticated: false,
    username: '',
    loading: true,
  });

  // On mount: verify cookie with server (no localStorage/sessionStorage)
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_ADMIN}/check_auth.php`, {
          method: 'GET',
          credentials: 'include', // send httpOnly cookie
        });
        const json = await res.json();
        setState({
          authenticated: json.authenticated === true,
          username: json.username || '',
          loading: false,
        });
      } catch {
        setState({ authenticated: false, username: '', loading: false });
      }
    })();
  }, []);

  const login = async (username: string, password: string) => {
    try {
      const res = await fetch(`${API_ADMIN}/login.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // receive httpOnly cookie
        body: JSON.stringify({ username, password }),
      });
      const json = await res.json();

      if (json.status === 'success') {
        setState({ authenticated: true, username, loading: false });
        return { success: true, message: json.message };
      }
      return { success: false, message: json.message || 'Login failed.' };
    } catch {
      return { success: false, message: 'Network error. Please try again.' };
    }
  };

  const logout = async () => {
    try {
      await fetch(`${API_ADMIN}/logout.php`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // continue regardless
    }
    setState({ authenticated: false, username: '', loading: false });
  };

  return (
    <AuthContext.Provider value={{ ...state, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};
